import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import LabelWrapper from "components/Common/LabelWrapper";
import { useRole } from "components/Hooks/UserHooks";
import CountUp from "react-countup";
import { getPushLogs } from "store/push-notification/thunk";
import CollapseFilter from "components/Common/CollapseFilter";
import DatePickerCustom from "components/Common/DatePickerCustom";
import {
  COLOR_NOTIFY_TYPE,
  COLOR_OPEN_STATUS,
  PUSH_LOG_NOTIFY_TYPE,
  PUSH_LOG_OPEN_STATUS,
  LIST_OPTION_OS,
  PUSH_LOG_PUSH_STATUS,
  COLOR_PUSH_LOG_STATUS,
  COLOR_USER_TYPE_LOG,
  USER_TYPE_LOG_OPTION,
  MANUAL_PUSH_SAVE_OPTION,
  COLOR_MANUAL_PUSH_SAVE_OPTION,
  MANUAL_PUSH_REDIRECT_SCREEN_TARGET,
} from "helpers/constans";
import { formatDateStringToKorea, formatDateStringToUTC, getPlatFormImage } from "helpers/format";
import CollapseContent from "components/Common/CollapseContent";
import { capitalizeFirstLetter, truncate } from "utils";
import TooltipCustom from "components/Common/TooltipCustom";
import DropdownOptionImage from "components/Common/DropdownOptionImage";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import CopyWrapper from "components/Common/CopyWrapper";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import IPHONE_MODELS from "helpers/iphone_models";

const TYPE_SELECT_DEFAULT: string = "createdAt";

registerLocale("en", en);
registerLocale("ko", ko);

const PushLog = () => {
  const { t, i18n } = useTranslation();
  const dispatch: any = useDispatch();
  const { userPermissions } = useRole();

  const [query, setQuery]: any = useQueryParams({
    time_request: withDefault(NumberParam, 0),
    page: withDefault(NumberParam, 1),
    limit: withDefault(NumberParam, 30),
    title: withDefault(StringParam, ""),
    body: withDefault(StringParam, ""),
    uuid: withDefault(StringParam, ""),
    email: withDefault(StringParam, ""),
    emailSNS: withDefault(StringParam, ""),
    fullName: withDefault(StringParam, ""),
    os: withDefault(StringParam, ""),
    type: withDefault(StringParam, ""),
    pushType: withDefault(StringParam, ""),
    pushStatus: withDefault(StringParam, ""),
    openStatus: withDefault(StringParam, ""),
    userType: withDefault(StringParam, ""),
    saveOption: withDefault(StringParam, ""),
    startDate: withDefault(
      StringParam,
      formatDateStringToUTC(
        moment(new Date()).startOf("day").format("Y-MM-DD HH:mm:ss")
      )
    ),
    endDate: withDefault(
      StringParam,
      formatDateStringToUTC(
        moment(new Date()).endOf("day").format("Y-MM-DD HH:mm:ss")
      )
    ),
  });

  const [titleSearch, setTitleSearch] = useState<string>(query?.title || "");
  const [contentSearch, setContentSearch] = useState<string>(query?.body || "");
  const [uuidSearch, setUUIDSearch] = useState<string>(query?.uuid || "");
  const [emailSearch, setEmailSearch] = useState<string>(query?.emailSNS || "");
  const [emailProfileSearch, setEmailProfileSearch] = useState<string>(query?.email || "");
  const [fullNameSearch, setFullNameSearch] = useState<string>(query?.fullName || "");

  const [isGuestSearch, setIsGuestSearch] = useState<any>(
    USER_TYPE_LOG_OPTION?.find((item) => item?.value === query?.userType)
      ?.value ?? ""
  );

  const [oSSearch, setOSSearch] = useState<any | null>(
    LIST_OPTION_OS?.filter(
      (item) => String(item?.value) === String(query?.os)
    )[0]?.value || ""
  );

  const [dataTypeSearch, setDataTypeSearch] = useState<any>(
    MANUAL_PUSH_REDIRECT_SCREEN_TARGET?.filter(
      (item) => String(item?.value) === String(query?.["type"])
    )[0]?.value || ""
  );

  const [notifyTypeSearch, setNotifyTypeSearch] = useState<any>(
    PUSH_LOG_NOTIFY_TYPE?.filter(
      (item) => String(item?.value) === String(query?.["pushType"])
    )[0]?.value || ""
  );

  const [pushStatusSearch, setPushStatusSearch] = useState<any>(
    PUSH_LOG_PUSH_STATUS?.find(
      (item) => String(item?.value) === String(query?.["pushStatus"])
    )?.value || ""
  );

  const [openStatusSearch, setOpenStatusSearch] = useState<any>(
    PUSH_LOG_OPEN_STATUS?.filter(
      (item) => item?.value === parseInt(query?.["openStatus"])
    )[0]?.value || ""
  );

  const [saveOptionSearch, setSaveOptionSearch] = useState<any>(
    MANUAL_PUSH_SAVE_OPTION?.find((item) => String(item?.value) === String(query?.saveOption))
      ?.value || ""
  );

  const [dateSearch, setDateSearch] = useState<any[]>([
    moment(new Date(), "Y-MM-DD HH:mm:ss").toDate(),
    moment(new Date(), "Y-MM-DD HH:mm:ss").toDate(),
  ]);
  const [startDate, endDate] = dateSearch;

  const selectLayoutState = (state: any) => state.PushNotification;
  const PushLogProperties = createSelector(selectLayoutState, (state) => {
    return {
      pushLogs: state.pushLogs,
      isPushLogLoading: state.isPushLogLoading,
      isPushLogSuccess: state.isPushLogSuccess,

      error: state.error,
    };
  });

  // Inside your component
  const { pushLogs, isPushLogLoading } = useSelector(PushLogProperties);

  function loadTableData() {
    dispatch(getPushLogs(query));
  }

  useEffect(() => {
    loadTableData();
  }, [dispatch, JSON.stringify(query)]);

  const searchData = () => {
    setQuery({
      ...query,
      title: titleSearch || "",
      body: contentSearch || "",
      uuid: uuidSearch || "",
      email: emailProfileSearch || "",
      emailSNS: emailSearch || "",
      fullName: fullNameSearch || "",
      os: oSSearch || "",
      type: dataTypeSearch || "",
      pushType: notifyTypeSearch || "",
      pushStatus: pushStatusSearch || "",
      openStatus: (openStatusSearch === 1) ? true : (openStatusSearch === 0) ? false : "",
      userType: isGuestSearch,
      saveOption: saveOptionSearch,
      startDate: startDate
        ? formatDateStringToUTC(
          moment(new Date(startDate))
            .startOf("day")
            .format("Y-MM-DD HH:mm:ss")
        )
        : "",
      endDate: endDate
        ? formatDateStringToUTC(
          moment(new Date(endDate)).endOf("day").format("Y-MM-DD HH:mm:ss")
        )
        : "",
      sortBy: TYPE_SELECT_DEFAULT,
      time_request: +new Date(),
      page: 1,
    });
  };

  const resetData = () => {
    setQuery(
      {
        title: "",
        body: "",
        uuid: "",
        email: "",
        emailSNS: "",
        fullName: "",
        os: "",
        pushType: "",
        type: "",
        pushStatus: "",
        openStatus: "",
        userType: "",
        saveOption: "",
        startDate: formatDateStringToUTC(
          moment(new Date()).startOf("day").format("Y-MM-DD HH:mm:ss")
        ),
        endDate: formatDateStringToUTC(
          moment(new Date()).endOf("day").format("Y-MM-DD HH:mm:ss")
        ),
        sortBy: "",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setTitleSearch("");
    setContentSearch("");
    setUUIDSearch("");
    setEmailSearch("");
    setEmailProfileSearch("");
    setFullNameSearch("");
    setOSSearch("");
    setIsGuestSearch("");
    setDataTypeSearch("");
    setPushStatusSearch("");
    setOpenStatusSearch("");
    setSaveOptionSearch("");
    setNotifyTypeSearch(null);
    setDateSearch([null, null]);
    setDateSearch([
      moment(`${moment().format("Y-MM-DD")}`, "Y-MM-DD").toDate(),
      moment(`${moment().format("Y-MM-DD")}`, "Y-MM-DD").toDate(),
    ]);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return (
            <div>
              {pushLogs?.pagination?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Open Type"),
        accessor: "type",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 180,
        Cell: (cell: any) => {
          const type = cell?.value;
          return cell?.value && (
            <div className="" style={{ minWidth: 150 }}>
              {t(MANUAL_PUSH_REDIRECT_SCREEN_TARGET.find(
                (i: any) => i?.value == type
              )?.label || '')
              }
            </div>
          );
        },
      },
      {
        Header: t("Send Type"),
        accessor: "pushType",
        filterable: false,
        sortable: false,
        thWidth: 100,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status = cell?.value;

          return (
            <div className="text-center" style={{ minWidth: "80px" }}>
              <span
                className={`rounded-pill badge bg-${COLOR_NOTIFY_TYPE[status] || "secondary"
                  }`}
              >
                {
                  t(PUSH_LOG_NOTIFY_TYPE?.find(
                    (item: any) => item?.value === status
                  )?.label || "")
                }
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Send Status"),
        accessor: "pushStatus",
        filterable: false,
        sortable: false,
        thWidth: 100,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status = cell?.value;
          const detailError = cell?.row?.original?.detailError;
          const msgError =
            i18n?.language === "en" ? detailError?.en : detailError?.ko;
          const minWidth: string | number =
            i18n?.language === "en" ? "125px" : "96px";

          return (
            <div className="position-relative" style={{ minWidth: minWidth }}>
              <div className="text-center" style={{ lineHeight: "16px" }}>
                <span
                  className={`rounded-pill badge bg-${COLOR_PUSH_LOG_STATUS[status] || "secondary"
                    }`}
                >
                  {t(
                    PUSH_LOG_PUSH_STATUS?.find(
                      (item: any) => String(item?.value) === String(status)
                    )?.label ?? ""
                  )}
                </span>
              </div>
              {status === "failed" && msgError && (
                <TooltipCustom
                  title={t(msgError)}
                  className="d-inline-block vertical-align-middle ms-1 thumbnail-mt"
                  style={{
                    transform: "translateY(2px)",
                    position: "absolute",
                    top: -3,
                    right: 0,
                    paddingLeft: 4,
                    paddingRight: 3,
                  }}
                >
                  <i
                    className="ri-question-line align-bottom text-secondary"
                    style={{ fontSize: "16px" }}
                  ></i>
                </TooltipCustom>
              )}
            </div>
          );
        },
      },
      {
        Header: t("Open Status"),
        accessor: "isSeen",
        filterable: false,
        sortable: false,
        thWidth: 100,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status = cell?.value ? 1 : 0;
          return (
            <div className="text-center" style={{ minWidth: "80px" }}>
              <span
                className={`rounded-pill badge bg-${COLOR_OPEN_STATUS[status] || "secondary"
                  }`}
              >
                {
                  t(PUSH_LOG_OPEN_STATUS?.find(
                    (item: any) => item?.value == status
                  )?.label || "")
                }
              </span>
            </div>
          );
        },
      },
      {
        Header: t("User Type"),
        accessor: "userType",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const userType = cell?.value;
          const userTypeText = USER_TYPE_LOG_OPTION?.find(
            (item: any) => String(item?.value) === String(userType)
          )?.label;

          return (
            <div style={{ minWidth: 100 }}>
              {
                userTypeText ? (
                  <div style={{ minWidth: 80 }}>
                    <span
                      className={`rounded-pill badge bg-${COLOR_USER_TYPE_LOG[userType] || "secondary"}`}
                    >
                      {t(userTypeText || "")}
                    </span>
                  </div>
                ) : (t(capitalizeFirstLetter(userType)))
              }
            </div>
          );
        },
      },
      {
        Header: t("Save Notification"),
        accessor: "saveOption",
        filterable: false,
        sortable: false,
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          const status = cell?.value;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${COLOR_MANUAL_PUSH_SAVE_OPTION[status] || "secondary"
                  }`}
              >
                {t(
                  MANUAL_PUSH_SAVE_OPTION?.find(
                    (item: any) => (item?.value) === (status)
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Full Name"),
        accessor: "targetUser.profile.fullname",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 160,
        Cell: (cell: any) => {
          let nickname = cell?.value;
          return (
            nickname ? (
              <div className="cursor-pointer" style={{ minWidth: 140 }}>
                <CopyWrapper contentCopy={nickname}>
                  {`${nickname}`}
                </CopyWrapper>
              </div>
            ) : <div className="cursor-pointer" style={{ minWidth: 140 }}>-</div>
          );
        },
      },
      {
        Header: t("Email Profile"),
        accessor: "email",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 160,
        Cell: (cell: any) => {
          let email = cell?.value;
          return (
            email ? (
              <div className="cursor-pointer" style={{ minWidth: 140 }}>
                <CopyWrapper contentCopy={email}>{`${email}`}</CopyWrapper>
              </div>
            ) : <div className="cursor-pointer" style={{ minWidth: 140 }}>-</div>
          );
        },
      },
      {
        Header: t("Email SNS"),
        accessor: "emailSNS",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 160,
        Cell: (cell: any) => {
          let email = cell?.value;
          return (
            email ? (
              <div className="cursor-pointer" style={{ minWidth: 140 }}>
                <CopyWrapper contentCopy={email}>{`${email}`}</CopyWrapper>
              </div>
            ) : <div className="cursor-pointer" style={{ minWidth: 140 }}>-</div>
          );
        },
      },
      {
        Header: t("OS"),
        accessor: "deviceOs",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 140,
        Cell: (cell: any) => {
          const deviceOS = cell?.value;
          let imgOS = getPlatFormImage(deviceOS);

          return (
            deviceOS && (
              <div
                className="d-flex align-items-center"
                style={{ minWidth: "110px" }}
              >
                {imgOS ? (
                  <div style={{ width: "25px" }} className="me-1">
                    {imgOS ? (
                      <img src={imgOS} width={20} height={20} alt="platform" />
                    ) : (
                      <span
                        className={`bg-secondary`}
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "3px",
                        }}
                      ></span>
                    )}
                  </div>
                ) : (
                  ""
                )}
                <div>
                  {t(
                    LIST_OPTION_OS?.find((item) => item?.value === deviceOS)
                      ?.label ?? ""
                  )}
                </div>
              </div>
            )
          );
        },
      },

      {
        Header: t("Device Model"),
        accessor: "deviceModel",
        filterable: true,
        sortable: false,
        thClass: "whitespace-nowrap",
        thWidth: 160,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          let deviceModel = item?.deviceModel;
          const os = item?.deviceOs;
          if (os === 'ios') {
            deviceModel = deviceModel ? (IPHONE_MODELS[deviceModel as keyof typeof IPHONE_MODELS] ?? deviceModel) : '';
          }

          return (
            deviceModel && (
              <div className="cursor-pointer" style={{ width: 150 }}>
                {t(`${deviceModel}`)}
              </div>
            )
          );
        },
      },

      {
        Header: t("Device Manufacturer"),
        accessor: "deviceManufacturer",
        filterable: true,
        sortable: false,
        thWidth: 170,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const manufacturer = cell?.value;
          return (
            manufacturer ? (
              <div className="cursor-pointer" style={{ minWidth: "160px" }}>
                <CopyWrapper contentCopy={manufacturer}>
                  {`${manufacturer}`}
                </CopyWrapper>
              </div>
            ) : "-"
          );
        },
      },

      {
        Header: t("UUID"),
        accessor: "udid",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const udid = cell?.value;
          return (
            udid ? (
              <div className="cursor-pointer" style={{ minWidth: "200px" }}>
                <CopyWrapper contentCopy={udid}>
                  {`${udid}`}
                </CopyWrapper>
              </div>
            ) : "-"
          );
        },
      },
      {
        Header: t("FCM Token"),
        accessor: "deviceToken",
        thClass: "whitespace-nowrap",
        filterable: true,
        sortable: false,
        thWidth: 340,
        Cell: (cell: any) => {
          const token = cell?.row?.original?.deviceToken;

          return (
            token && (
              <div className="cursor-pointer" style={{ maxWidth: "330px" }}>
                <CopyWrapper contentCopy={token} style={{ minWidth: "70px", whiteSpace: "nowrap" }}>
                  {truncate(token, 36)}
                </CopyWrapper>
              </div>
            )
          );
        },
      },
      {
        Header: t("Title"),
        accessor: "title",
        filterable: true,
        sortable: false,
        thWidth: 240,
        Cell: (cell: any) => {
          let title = cell?.value;

          return title ? (
            <CopyWrapper contentCopy={title} style={{ minWidth: "220px" }}>
              <CollapseContent
                style={{ maxWidth: "250px" }}
                dataOriginal={title}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Title")}
              />
            </CopyWrapper>
          ) : "-";
        },
      },
      {
        Header: t("Content"),
        accessor: "body",
        filterable: true,
        sortable: false,
        thWidth: 250,
        Cell: (cell: any) => {
          let content = cell?.value;

          return content && (
            <CopyWrapper contentCopy={content} style={{ minWidth: "240px" }}>
              <CollapseContent
                style={{ maxWidth: "350px" }}
                dataOriginal={content}
                isRank={false}
                isInlineBlock={true}
                isShowModal={true}
                titleModal={t("Content")}
              />
            </CopyWrapper>
          );
        },
      },

      {
        Header: t("Created at"),
        accessor: "createdAt",
        filterable: true,
        sortable: false,
        thWidth: 130,
        thClass: "text-end whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "100px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, userPermissions, pushLogs?.pagination?.total]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  useEffect(() => {
    document.title = `${t("Push Log")} - ${t("Push Notification")} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("Push Log")} pageTitle={t("Home")} />
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={pushLogs?.pagination?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={t("Title")}
                          isShow={false}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Title")}...`}
                            value={titleSearch}
                            onChange={(e) => setTitleSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper
                          label={t("Content")}
                          isShow={false}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Content")}...`}
                            value={contentSearch}
                            onChange={(e) => setContentSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Full Name")} isShow={false}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Full Name")}...`}
                            value={fullNameSearch}
                            onChange={(e) => setFullNameSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Email SNS")} isShow={false}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email SNS")}...`}
                            value={emailSearch}
                            onChange={(e) => setEmailSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Email Profile")} isShow={false}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Email Profile")}...`}
                            value={emailProfileSearch}
                            onChange={(e) => setEmailProfileSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("UUID")} isShow={false}>
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("UUID")}...`}
                            value={uuidSearch}
                            onChange={(e) => setUUIDSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("OS")} isShow={true}>
                          <DropdownOptionImage
                            name="os"
                            dataList={LIST_OPTION_OS || []}
                            placeholder={`${t("OS")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={oSSearch || ""}
                            onChangeSelect={(e: any) => setOSSearch(e?.value)}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <div>
                          <LabelWrapper
                            label={t("Send Type")}
                            isShow={true}
                          >
                            <DropdownStatusNew
                              name="nofify-type"
                              dataList={PUSH_LOG_NOTIFY_TYPE || []}
                              placeholder={`${t("Send Type")}...`}
                              className="dropdown-status-rounded"
                              classNamePrefix="name-prefix"
                              initialValue={notifyTypeSearch || ""}
                              onChangeSelect={(e: any) =>
                                setNotifyTypeSearch(e?.value)
                              }
                              isHasOptionAll={true}
                              optionAll={{ label: t("All Filter"), value: "" }}
                              colors={COLOR_NOTIFY_TYPE}
                            />
                          </LabelWrapper>
                        </div>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <div>
                          <LabelWrapper
                            label={t("Open Type")}
                            isShow={true}
                          >
                            <DropdownOptionNew
                              name="notification_action"
                              dataList={MANUAL_PUSH_REDIRECT_SCREEN_TARGET || []}
                              placeholder={`${t("Open Type")}...`}
                              className="dropdown-status-rounded"
                              classNamePrefix="name-prefix"
                              initialValue={dataTypeSearch || ""}
                              onChangeSelect={(e: any) => setDataTypeSearch(e?.value)}
                              isHasOptionAll={true}
                              optionAll={{ label: t("All Filter"), value: "" }}
                            />
                          </LabelWrapper>
                        </div>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Send Status")} isShow={true}>
                          <DropdownStatusNew
                            name="push-status"
                            dataList={PUSH_LOG_PUSH_STATUS || []}
                            placeholder={`${t("Send Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={pushStatusSearch || ""}
                            onChangeSelect={(e: any) => setPushStatusSearch(e?.value)}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_PUSH_LOG_STATUS}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Open Status")} isShow={true}>
                          <DropdownStatusNew
                            name="open-status"
                            dataList={PUSH_LOG_OPEN_STATUS || []}
                            placeholder={`${t("Open Status")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={openStatusSearch}
                            onChangeSelect={(e: any) => setOpenStatusSearch(e?.value)}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_OPEN_STATUS}
                          // isIntegerValue={true}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("User Type")} isShow={true}>
                          <DropdownStatusNew
                            name="type-user"
                            dataList={USER_TYPE_LOG_OPTION || []}
                            placeholder={`${t("User Type")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={isGuestSearch}
                            onChangeSelect={(e: any) =>
                              setIsGuestSearch(e?.value)
                            }
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_USER_TYPE_LOG}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Save Notification")} isShow={true}>
                          <DropdownStatusNew
                            name="open-status"
                            dataList={MANUAL_PUSH_SAVE_OPTION || []}
                            placeholder={`${t("Save Notification")}...`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={saveOptionSearch}
                            onChangeSelect={(e: any) => setSaveOptionSearch(e?.value)}
                            isHasOptionAll={true}
                            optionAll={{ label: t("All Filter"), value: "" }}
                            colors={COLOR_MANUAL_PUSH_SAVE_OPTION}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={6}
                        lg={6}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-4 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={pushLogs?.list?.length ? pushLogs?.list : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={pushLogs?.pagination?.total}
                      customPageCount={Math.ceil(
                        Number(pushLogs?.pagination?.total) / Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isPushLogLoading}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PushLog;
